import {
  Dialog,
  DialogActions as MaterialDialogActions,
  DialogContent,
  DialogTitle,
} from '@material-ui/core';
import {Button} from '@wandb/weave/components/Button';
import {Tag} from '@wandb/weave/components/Tag';
import React, {useState} from 'react';
import {useHistory} from 'react-router-dom';
import styled from 'styled-components';

import {CopyableId} from '../../common/Id';
import {TraceObjSchemaForBaseObjectClass} from '../../wfReactInterface/objectClassQuery';
import {LLMMaxTokensKey} from '../llmMaxTokens';
import {
  OptionalTraceCallSchema,
  PlaygroundState,
  SavedPlaygroundModelState,
} from '../types';
import {
  DEFAULT_SAVED_MODEL,
  DEFAULT_SYSTEM_MESSAGE,
} from '../usePlaygroundState';
import {LLMDropdown, ProviderOption} from './LLMDropdown';
import {setLastUsedModel} from './LLMDropdown/modelCache';
import {
  SetPlaygroundStateFieldFunctionType,
  TraceCallOutput,
} from './useChatFunctions';

type PlaygroundChatTopBarProps = {
  idx: number;
  settingsTab: number | null;
  setSettingsTab: (tab: number | null) => void;
  setPlaygroundStateField: SetPlaygroundStateFieldFunctionType;
  entity: string;
  project: string;
  playgroundStates: PlaygroundState[];
  setPlaygroundStates: (playgroundStates: PlaygroundState[]) => void;
  isTeamAdmin: boolean;
  refetchConfiguredProviders: () => void;
  refetchCustomLLMs: () => void;
  llmDropdownOptions: ProviderOption[];
  areProvidersLoading: boolean;
  customProvidersResult: TraceObjSchemaForBaseObjectClass<'Provider'>[];
};

const DialogActions = styled(MaterialDialogActions)<{$align: string}>`
  justify-content: ${({$align}) =>
    $align === 'left' ? 'flex-start' : 'flex-end'} !important;
  padding: 32px 32px 32px 32px !important;
`;
DialogActions.displayName = 'S.DialogActions';

export const PlaygroundChatTopBar: React.FC<PlaygroundChatTopBarProps> = ({
  idx,
  settingsTab,
  setSettingsTab,
  setPlaygroundStateField,
  entity,
  project,
  playgroundStates,
  setPlaygroundStates,
  isTeamAdmin,
  refetchConfiguredProviders,
  refetchCustomLLMs,
  llmDropdownOptions,
  areProvidersLoading,
  customProvidersResult,
}) => {
  const history = useHistory();
  const onlyOneChat = playgroundStates.length === 1;
  const [confirmClear, setConfirmClear] = useState(false);

  const clearCall = (index: number) => {
    history.push(`/${entity}/${project}/weave/playground`);
    setPlaygroundStateField(index, 'traceCall', {
      project_id: `${entity}/${project}`,
      id: '',
      inputs: {
        messages: [DEFAULT_SYSTEM_MESSAGE],
      },
    } as OptionalTraceCallSchema);
  };

  const handleModelChange = (
    index: number,
    model: LLMMaxTokensKey,
    maxTokens: number,
    maxTokensDefault: number,
    savedModel?: SavedPlaygroundModelState
  ) => {
    // Persist the model selection to localStorage
    setLastUsedModel(entity, project, model);

    if (!savedModel) {
      setPlaygroundStates(
        playgroundStates.map((state, i) => {
          if (i === index) {
            return {
              ...state,
              model,
              maxTokensLimit: maxTokens,
              maxTokens: maxTokensDefault,
            };
          }
          return state;
        })
      );
      return;
    }

    const {messagesTemplate, ...defaultParams} =
      savedModel?.savedModelParams ?? {};

    // Filter out undefined values from defaultParams
    const filteredDefaultParams = Object.fromEntries(
      Object.entries(defaultParams).filter(([_, value]) => value !== undefined)
    );

    setPlaygroundStates(
      playgroundStates.map((state, i) => {
        if (i === index) {
          return {
            ...state,
            model,
            maxTokensLimit: maxTokens,
            maxTokens: maxTokensDefault,

            // Update the state to show we are using a saved model
            savedModel: savedModel ?? DEFAULT_SAVED_MODEL,
            traceCall: {
              ...state.traceCall,
              inputs: {
                ...state.traceCall?.inputs,
                // If the saved model has messages, use them, otherwise use the current messages
                messages: messagesTemplate ?? state.traceCall?.inputs?.messages,
              },
              output: {
                ...(state.traceCall?.output as TraceCallOutput),
                // If the saved model has messages, clear the choices, otherwise use the current choices
                choices: messagesTemplate
                  ? undefined
                  : (state.traceCall?.output as TraceCallOutput)?.choices,
              },
            },
            // Update the other params
            ...filteredDefaultParams,
          };
        }
        return state;
      })
    );
  };

  const ConfirmClearModal: React.FC<{
    open: boolean;
    onClose: () => void;
    onConfirm: () => void;
  }> = ({open, onClose, onConfirm}) => {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Clear chat history</DialogTitle>
        <DialogContent style={{overflow: 'hidden'}}>
          <p>Are you sure you want to clear the chat history?</p>
        </DialogContent>
        <DialogActions $align="left">
          <Button variant="destructive" onClick={onConfirm}>
            Clear history
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <div className="flex w-full flex-col gap-3">
      {/* First row: Number, model icon, model name, close button */}
      <div className="flex w-full justify-between items-center">
        <div className="flex items-center gap-3">
          <Tag label={`${idx + 1}`} />
          <Button icon="model" variant="ghost" size="medium" disabled />
          <span className="text-medium text-moon-600">
            {playgroundStates[idx].savedModel?.objectId || 'Untitled model'}
          </span>
        </div>

        <div className="flex items-center gap-2">
          {playgroundStates[idx].traceCall?.id && (
            <div className="flex items-center">
              <CopyableId
                id={playgroundStates[idx]!.traceCall!.id!}
                type="Call"
              />
            </div>
          )}
          <Button
            tooltip={'Clear chat'}
            icon="randomize-reset-reload"
            size="medium"
            variant="ghost"
            onClick={() => setConfirmClear(true)}
          />
          <Button
            endIcon="close"
            size="medium"
            variant="ghost"
            disabled={onlyOneChat}
            tooltip={onlyOneChat ? 'Cannot remove last chat' : 'Remove chat'}
            onClick={() => {
              // If the settings tab is set to length that is going to be removed,
              // we need to set the settings tab to the previous chat.
              if (settingsTab && settingsTab < playgroundStates.length + 1) {
                setSettingsTab(settingsTab - 1);
              }
              setPlaygroundStates(
                playgroundStates.filter((_, index) => index !== idx)
              );
            }}
          />
        </div>
      </div>

      {/* Divider */}
      <div className="border-t border-moon-200 mb-8"></div>

      {/* Second row: LLM dropdown, settings button, settings summary */}
      <div className="flex w-full items-center gap-4 mb-8">
        <LLMDropdown
          value={playgroundStates[idx].model}
          onChange={(model, maxTokens, maxTokensDefault, savedModel) =>
            handleModelChange(
              idx,
              model as LLMMaxTokensKey,
              maxTokens,
              maxTokensDefault,
              savedModel
            )
          }
          entity={entity}
          project={project}
          isTeamAdmin={isTeamAdmin}
          refetchConfiguredProviders={refetchConfiguredProviders}
          refetchCustomLLMs={refetchCustomLLMs}
          llmDropdownOptions={llmDropdownOptions}
          areProvidersLoading={areProvidersLoading}
          customProvidersResult={customProvidersResult}
          direction={{horizontal: 'right'}}
          selectFirstAvailable={true}
          className="w-[300px]"
        />
        <Button
          tooltip={'Chat settings'}
          icon="settings-parameters"
          size="medium"
          variant="ghost"
          active={settingsTab === idx}
          onClick={() => {
            if (settingsTab === idx) {
              setSettingsTab(null);
            } else {
              setSettingsTab(idx);
            }
          }}
        />
        <div className="flex items-center gap-2 text-xs text-moon-500 overflow-hidden">
          <span className="whitespace-nowrap">
            Num trials: {playgroundStates[idx].nTimes},
          </span>
          <span className="whitespace-nowrap">
            Temperature: {playgroundStates[idx].temperature},
          </span>
          <span className="whitespace-nowrap truncate">
            Response format: {playgroundStates[idx].responseFormat}
          </span>
        </div>
      </div>
      <ConfirmClearModal
        open={confirmClear}
        onClose={() => setConfirmClear(false)}
        onConfirm={() => {
          clearCall(idx);
          setConfirmClear(false);
        }}
      />
    </div>
  );
};
